import {
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Container,
  Divider,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { TOKEN_KEY, url, imgUrl } from 'src/Helpers/utils/Constant';
import { TableLoader, TableNotFound } from 'src/Utils/TableLoader';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { Edit } from '@mui/icons-material';
import DeleteConfirmation from 'src/Utils/DeleteConfirmation';
import ModalsWrapper from 'src/components/Modals/ModalsWrapper';
import CreateImportantMessage from './CreateImportantMessage';
import UpdateImportantMessage from './UpdateImportantMessage';
import { IImportantMessage } from './types';
import Toaster from 'src/Toaster/Toaster';

function ImportantMessage() {
  const theme = useTheme();
  const Toast = Toaster();

  const [loading, setLoading] = useState(false);
  const [messageList, setMessageList] = useState<IImportantMessage[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const token = localStorage.getItem(TOKEN_KEY);

  // ▶ Create Modal state
  const [createOpen, setCreateOpen] = useState(false);
  const handleCreateOpen = () => setCreateOpen(true);
  const handleCreateClose = () => setCreateOpen(false);

  // ▶ Delete Modal state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<IImportantMessage | null>(
    null
  );

  // ▶ Edit Modal state
  const [editOpen, setEditOpen] = useState(false);
  const [messageToUpdate, setMessageToUpdate] = useState<IImportantMessage | null>(null);

  // ▶ Status updating state
  const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);

  // Fetch important messages
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${url}/api/v1/admin/important-message`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (data.success && Array.isArray(data.data)) {
          setMessageList(data.data);
        } else {
          setMessageList([]);
        }
      } catch (error) {
        console.error('Failed to fetch important messages', error);
        setMessageList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [refreshTrigger, token]);

  // Open Delete Modal
  const handleOpenDelete = (msg: IImportantMessage) => {
    setSelectedMessage(msg);
    setDeleteOpen(true);
  };

  // Close Delete Modal
  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setSelectedMessage(null);
  };

  // Delete Important Message
  const handleDelete = async () => {
    if (!selectedMessage?.id) return;
    try {
      setDeleteLoading(true);

      const { data } = await axios.delete(
        `${url}/api/v1/admin/important-message/${selectedMessage.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data.success) {
        Toast.fire({
          icon: 'success',
          title: data.message || 'Important message deleted successfully!',
        });
        setRefreshTrigger((prev) => prev + 1);
        handleCloseDelete();
      } else {
        Toast.fire({
          icon: 'error',
          title: data.message || 'Failed to delete important message.',
        });
      }
    } catch (error: any) {
      console.error('Failed to delete important message', error);
      Toast.fire({
        icon: 'error',
        title: error?.response?.data?.message || 'Error deleting important message.',
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Toggle Status Switch
  const handleToggleStatus = async (msg: IImportantMessage) => {
    const nextStatus = msg.status === 1 ? 0 : 1;
    try {
      setUpdatingStatusId(msg.id);

      const { data } = await axios.patch(
        `${url}/api/v1/admin/important-message/${msg.id}`,
        { status: nextStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data.success) {
        Toast.fire({
          icon: 'success',
          title: data.message || `Status updated to ${nextStatus === 1 ? 'Active' : 'Inactive'}!`,
        });
        setRefreshTrigger((prev) => prev + 1);
      } else {
        Toast.fire({
          icon: 'error',
          title: data.message || 'Failed to update status.',
        });
      }
    } catch (error: any) {
      console.error('Failed to update status', error);
      Toast.fire({
        icon: 'error',
        title: error?.response?.data?.message || 'Error updating status.',
      });
    } finally {
      setUpdatingStatusId(null);
    }
  };

  return (
    <Container maxWidth='xl'>
      <Card>
        <CardHeader
          action={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Button
                onClick={handleCreateOpen}
                variant='contained'
                sx={{ ml: 2 }}
              >
                Create Important Message
              </Button>
            </Box>
          }
          title='Important Message List'
        />
        <Divider />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>SL.</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Designation</TableCell>
                <TableCell sx={{ maxWidth: 300 }}>Message</TableCell>
                <TableCell align='center'>Order</TableCell>
                <TableCell align='center'>Status</TableCell>
                <TableCell align='center'>Toggle Status</TableCell>
                <TableCell align='center'>Action</TableCell>
              </TableRow>
            </TableHead>

            {loading ? (
              <TableLoader />
            ) : messageList?.length ? (
              <TableBody>
                {messageList.map((msg: IImportantMessage, i: number) => (
                  <TableRow hover key={msg.id || i}>
                    {/* SL */}
                    <TableCell>
                      <Typography variant='body1' color='text.primary' gutterBottom noWrap>
                        {i + 1}
                      </Typography>
                    </TableCell>

                    {/* Image */}
                    <TableCell>
                      {msg?.image ? (
                        <Box
                          component='img'
                          src={`${imgUrl}/${msg.image}`}
                          alt={msg.name}
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: 1,
                            objectFit: 'cover',
                            boxShadow: 1,
                          }}
                        />
                      ) : (
                        <Typography variant='body2' color='text.secondary'>
                          No Image
                        </Typography>
                      )}
                    </TableCell>

                    {/* Name */}
                    <TableCell>
                      <Typography variant='body1' color='text.primary' gutterBottom noWrap>
                        {msg?.name}
                      </Typography>
                    </TableCell>

                    {/* Designation */}
                    <TableCell>
                      <Typography variant='body1' color='text.primary' gutterBottom noWrap>
                        {msg?.designation || 'N/A'}
                      </Typography>
                    </TableCell>

                    {/* Message */}
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography
                        variant='body1'
                        color='text.primary'
                        gutterBottom
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }}
                      >
                        {msg?.message}
                      </Typography>
                    </TableCell>

                    {/* Order */}
                    <TableCell align='center'>
                      <Typography variant='body1' color='text.primary' gutterBottom noWrap>
                        {msg?.order ?? msg?.rank}
                      </Typography>
                    </TableCell>

                    {/* Status Label */}
                    <TableCell align='center'>
                      <Chip
                        label={Number(msg?.status) === 1 ? 'Active' : 'Inactive'}
                        color={Number(msg?.status) === 1 ? 'success' : 'error'}
                        size='small'
                        sx={{
                          textTransform: 'capitalize',
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>

                    {/* Toggle Status Switch */}
                    <TableCell align='center'>
                      <Switch
                        checked={Number(msg?.status) === 1}
                        onChange={() => handleToggleStatus(msg)}
                        disabled={updatingStatusId === msg.id}
                        color='primary'
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell align='center'>
                      {/* Edit */}
                      <Tooltip title='Edit' arrow>
                        <IconButton
                          onClick={() => {
                            setMessageToUpdate(msg);
                            setEditOpen(true);
                          }}
                          sx={{
                            '&:hover': {
                              background: theme.colors.primary.lighter,
                            },
                            color: theme.palette.primary.main,
                            mr: 1,
                          }}
                          color='inherit'
                          size='small'
                        >
                          <Edit fontSize='small' />
                        </IconButton>
                      </Tooltip>

                      {/* Delete */}
                      <Tooltip title='Delete' arrow>
                        <IconButton
                          onClick={() => handleOpenDelete(msg)}
                          sx={{
                            '&:hover': {
                              background: theme.colors.error.lighter,
                            },
                            color: theme.palette.error.main,
                          }}
                          color='inherit'
                          size='small'
                        >
                          <DeleteTwoToneIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableNotFound />
            )}
          </Table>
        </TableContainer>

        {/* CREATE MODAL */}
        <ModalsWrapper
          setShowModal={setCreateOpen}
          showModal={createOpen}
          modalData={{ title: 'Create Important Message' }}
          maxWidth='sm'
        >
          <CreateImportantMessage
            handleClose={handleCreateClose}
            setRefreshTrigger={setRefreshTrigger}
          />
        </ModalsWrapper>

        {/* EDIT MODAL */}
        <ModalsWrapper
          setShowModal={(val: boolean) => {
            setEditOpen(val);
            if (!val) {
              setMessageToUpdate(null);
            }
          }}
          showModal={editOpen}
          modalData={{ title: 'Update Important Message' }}
          maxWidth='sm'
        >
          {messageToUpdate && (
            <UpdateImportantMessage
              key={messageToUpdate.id}
              messageItem={messageToUpdate}
              handleClose={() => {
                setEditOpen(false);
                setMessageToUpdate(null);
              }}
              setRefreshTrigger={setRefreshTrigger}
            />
          )}
        </ModalsWrapper>

        {/* DELETE CONFIRMATION MODAL */}
        <DeleteConfirmation
          open={deleteOpen}
          handleClose={handleCloseDelete}
          text={
            selectedMessage
              ? `Want to delete the message from "${selectedMessage.name}"?`
              : 'Want to delete this message?'
          }
          handleDeleteFun={handleDelete}
          loading={deleteLoading}
        />
      </Card>
    </Container>
  );
}

export default ImportantMessage;
